import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ShieldAlert, RefreshCw, Search, Snowflake, Lock, CheckCircle2, UserMinus } from 'lucide-react';

type RiskCase = {
  user_id: string;
  email: string | null;
  user_type: string | null;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  score: number;
  indicators: string[];
  evidence: Record<string, unknown>;
  account_created_at: string | null;
  investigation_status: string;
  recommended_action: string | null;
  last_scored_at: string | null;
};

type ActionRow = {
  id: string;
  user_id: string | null;
  email: string | null;
  action: string;
  risk_level: string | null;
  reason: string;
  reviewer_email: string | null;
  investigation_status: string;
  created_at: string;
};

const LEVEL_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-slate-100 text-slate-700 border-slate-300'
};

const ACTIONS: { key: string; label: string; icon: typeof Lock; danger?: boolean }[] = [
  { key: 'require_reauth', label: 'Require reauth', icon: RefreshCw },
  { key: 'restrict_sensitive', label: 'Restrict', icon: Lock },
  { key: 'freeze_credits', label: 'Freeze credits', icon: Snowflake, danger: true },
  { key: 'suspend', label: 'Suspend', icon: ShieldAlert, danger: true },
  { key: 'revoke_admin', label: 'Revoke admin', icon: UserMinus, danger: true },
  { key: 'clear', label: 'Clear (false positive)', icon: CheckCircle2 }
];

export default function AdminAccountIntegrity() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [cases, setCases] = useState<RiskCase[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const call = async (body: Record<string, unknown>) => {
    const { data, error: err } = await supabase.functions.invoke('account-integrity', { body });
    if (err) throw new Error(err.message);
    if (data?.error) throw new Error(String(data.error));
    return data;
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await call({ action: 'list' });
      setCounts(data.counts || {});
      setTotal(data.total || 0);
      setCases(data.cases || []);
      setActions(data.actions || []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    setError('');
    try {
      await call({ action: 'scan' });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScanning(false);
    }
  };

  const act = async (userId: string, key: string) => {
    const reason = (reasons[userId] || '').trim();
    if (reason.length < 8) {
      setError('Document a reason (at least 8 characters) before restricting an account.');
      return;
    }
    setBusy(`${userId}:${key}`);
    setError('');
    try {
      await call({ action: 'act', user_id: userId, act: key, reason });
      setReasons((r) => ({ ...r, [userId]: '' }));
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldAlert className="h-5 w-5 text-orange-600" />
            Account integrity
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Server-side fraud, duplicate, automation and takeover scoring. Scores are computed in the database from
            evidence — never from anything the browser sends. Runs automatically every day at 07:00 UTC.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={scan}
            disabled={scanning}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <Search className={`h-4 w-4 ${scanning ? 'animate-pulse' : ''}`} />
            {scanning ? 'Scanning…' : 'Run scan now'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {(['critical', 'high', 'medium', 'low'] as const).map((lvl) => (
          <div key={lvl} className={`rounded-lg border px-3 py-2 ${LEVEL_STYLES[lvl]}`}>
            <div className="text-xs font-medium uppercase tracking-wide">{lvl}</div>
            <div className="text-xl font-semibold">{counts[lvl] ?? 0}</div>
          </div>
        ))}
        <div className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-600">Scored</div>
          <div className="text-xl font-semibold text-slate-900">{total}</div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {cases.length === 0 && !loading && (
          <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
            No medium-or-higher risk accounts stored yet. Run a scan to build the first snapshot.
          </p>
        )}

        {cases.map((c) => (
          <div key={c.user_id} className="rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${LEVEL_STYLES[c.risk_level]}`}>
                    {c.risk_level} · {c.score}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{c.user_type}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {c.investigation_status}
                  </span>
                </div>
                <p className="mt-2 break-all font-mono text-xs text-slate-500">{c.user_id}</p>
                <p className="text-sm font-medium text-slate-900">{c.email}</p>
                <p className="text-xs text-slate-500">
                  created {c.account_created_at ? new Date(c.account_created_at).toLocaleString() : '—'}
                </p>
              </div>
              <div className="max-w-md text-right">
                <p className="text-xs font-medium text-slate-700">Recommended</p>
                <p className="text-xs text-slate-600">{c.recommended_action}</p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {(c.indicators || []).map((i) => (
                <span key={i} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                  {i.replace(/_/g, ' ')}
                </span>
              ))}
            </div>

            {c.evidence && Object.keys(c.evidence).length > 0 && (
              <pre className="mt-3 overflow-x-auto rounded bg-slate-50 p-2 text-xs text-slate-600">
                {JSON.stringify(c.evidence, null, 2)}
              </pre>
            )}

            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                value={reasons[c.user_id] || ''}
                onChange={(e) => setReasons((r) => ({ ...r, [c.user_id]: e.target.value }))}
                placeholder="Reason for this decision (recorded with your name and timestamp)"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {ACTIONS.map(({ key, label, icon: Icon, danger }) => (
                <button
                  key={key}
                  onClick={() => act(c.user_id, key)}
                  disabled={busy === `${c.user_id}:${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium disabled:opacity-50 ${
                    danger
                      ? 'border-red-300 text-red-700 hover:bg-red-50'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {actions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-slate-900">Review log</h3>
          <p className="text-xs text-slate-500">
            Every restriction is stored with its reason, reviewer and timestamp. Records are append-only.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Action</th>
                  <th className="py-1 pr-3">Account</th>
                  <th className="py-1 pr-3">Reviewer</th>
                  <th className="py-1">Reason</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {actions.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="py-1 pr-3 font-medium">{a.action}</td>
                    <td className="py-1 pr-3">{a.email}</td>
                    <td className="py-1 pr-3">{a.reviewer_email}</td>
                    <td className="py-1">{a.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
